import {
  IconBolt,
  IconBuildingFactory2,
  IconCar,
  IconFlask,
  IconHeartRateMonitor,
  IconPick,
  IconShip,
  IconShirt,
  IconSparkles,
  IconTractor,
  IconTopologyStar3,
  IconTruckDelivery,
  type TablerIcon,
} from "@tabler/icons-react";

export type SectorVisualConfig = {
  icon: TablerIcon;
  iconColor: string;
  iconBg: string;
  accent: string;
  tagline: string;
};

const DEFAULT_VISUAL: SectorVisualConfig = {
  icon: IconSparkles,
  iconColor: "#04305c",
  iconBg: "#E2E8F0",
  accent: "#04305c",
  tagline: "Connecting businesses for growth opportunities.",
};

export const SECTOR_VISUAL_BY_SLUG: Record<string, SectorVisualConfig> = {
  "automotive-mobility-ecosystem": {
    icon: IconCar,
    iconColor: "#1D4ED8",
    iconBg: "#DBEAFE",
    accent: "#1D4ED8",
    tagline: "Connecting automotive and mobility businesses for growth opportunities.",
  },
  "industrial-automation-smart-manufacturing-ecosystem": {
    icon: IconBuildingFactory2,
    iconColor: "#6D28D9",
    iconBg: "#EDE9FE",
    accent: "#6D28D9",
    tagline: "Connecting smart manufacturing and industrial automation businesses.",
  },
  "textile-apparel-fashion-manufacturing-ecosystem": {
    icon: IconShirt,
    iconColor: "#DB2777",
    iconBg: "#FCE7F3",
    accent: "#DB2777",
    tagline: "Connecting textile, apparel and fashion manufacturing businesses.",
  },
  "renewable-energy-electrification-ecosystem": {
    icon: IconBolt,
    iconColor: "#D97706",
    iconBg: "#FEF3C7",
    accent: "#D97706",
    tagline: "Connecting renewable energy and electrification businesses.",
  },
  "food-beverage-fmcg-ecosystem": {
    icon: IconTruckDelivery,
    iconColor: "#EA580C",
    iconBg: "#FFEDD5",
    accent: "#EA580C",
    tagline: "Connecting food, beverage and FMCG businesses for partnerships.",
  },
  "healthcare-pharma-medtech-ecosystem": {
    icon: IconHeartRateMonitor,
    iconColor: "#059669",
    iconBg: "#D1FAE5",
    accent: "#059669",
    tagline: "Connecting healthcare, pharma and medtech businesses.",
  },
  "maritime-ports-offshore-ecosystem": {
    icon: IconShip,
    iconColor: "#0891B2",
    iconBg: "#CFFAFE",
    accent: "#0891B2",
    tagline: "Connecting maritime, ports and offshore businesses.",
  },
  "chemicals-materials-advanced-manufacturing-ecosystem": {
    icon: IconFlask,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    accent: "#16A34A",
    tagline: "Connecting chemicals and advanced manufacturing businesses.",
  },
  "infrastructure-construction-smart-cities-ecosystem": {
    icon: IconTopologyStar3,
    iconColor: "#475569",
    iconBg: "#E2E8F0",
    accent: "#475569",
    tagline: "Connecting infrastructure, construction and smart cities businesses.",
  },
  "agriculture-agritech-ecosystem": {
    icon: IconTractor,
    iconColor: "#65A30D",
    iconBg: "#ECFCCB",
    accent: "#65A30D",
    tagline: "Connecting agriculture and agrotech businesses.",
  },
  "mining-metals-resources-ecosystem": {
    icon: IconPick,
    iconColor: "#DC2626",
    iconBg: "#FEE2E2",
    accent: "#DC2626",
    tagline: "Connecting mining, metals and resources businesses.",
  },
  "digital-transformation-enterprise-technology-ecosystem": {
    icon: IconSparkles,
    iconColor: "#4F46E5",
    iconBg: "#E0E7FF",
    accent: "#4F46E5",
    tagline: "Connecting digital transformation and enterprise technology businesses.",
  },
};

export function getSectorVisual(slug: string): SectorVisualConfig {
  return SECTOR_VISUAL_BY_SLUG[slug] ?? DEFAULT_VISUAL;
}

/** Approximate x/y (%) positions on the simplified world map SVG. */
export const MAP_PIN_POSITIONS: Record<string, { x: number; y: number }> = {
  US: { x: 22, y: 38 },
  IN: { x: 68, y: 48 },
  GB: { x: 46, y: 30 },
  DE: { x: 50, y: 32 },
  FR: { x: 48, y: 34 },
  JP: { x: 82, y: 40 },
  KR: { x: 80, y: 38 },
  CN: { x: 74, y: 38 },
  AU: { x: 84, y: 72 },
  BR: { x: 32, y: 68 },
  AE: { x: 58, y: 46 },
  SG: { x: 74, y: 58 },
  CA: { x: 20, y: 28 },
  IT: { x: 50, y: 36 },
  NL: { x: 48, y: 31 },
};

export const DONUT_SEGMENT_COLORS = ["#2563EB", "#16A34A", "#7C3AED", "#F97316"];

const COMPANY_SEGMENT_DEFS = [
  { key: "manufacturers", label: "Manufacturers", percent: 23 },
  { key: "service_providers", label: "Service Providers", percent: 19 },
  { key: "technology_firms", label: "Technology Firms", percent: 13 },
  { key: "exporters", label: "Exporters", percent: 45 },
] as const;

/** Static company totals per sector — ~400 companies distributed across 12 sectors. */
export const STATIC_SECTOR_COMPANY_COUNTS: Record<string, number> = {
  "automotive-mobility-ecosystem": 40,
  "industrial-automation-smart-manufacturing-ecosystem": 60,
  "textile-apparel-fashion-manufacturing-ecosystem": 30,
  "renewable-energy-electrification-ecosystem": 38,
  "food-beverage-fmcg-ecosystem": 32,
  "healthcare-pharma-medtech-ecosystem": 40,
  "maritime-ports-offshore-ecosystem": 25,
  "chemicals-materials-advanced-manufacturing-ecosystem": 35,
  "infrastructure-construction-smart-cities-ecosystem": 30,
  "agriculture-agritech-ecosystem": 22,
  "mining-metals-resources-ecosystem": 20,
  "digital-transformation-enterprise-technology-ecosystem": 28,
};

/** Static country totals per sector — values between 4 and 6. */
export const STATIC_SECTOR_COUNTRY_COUNTS: Record<string, number> = {
  "automotive-mobility-ecosystem": 5,
  "industrial-automation-smart-manufacturing-ecosystem": 6,
  "textile-apparel-fashion-manufacturing-ecosystem": 4,
  "renewable-energy-electrification-ecosystem": 6,
  "food-beverage-fmcg-ecosystem": 5,
  "healthcare-pharma-medtech-ecosystem": 6,
  "maritime-ports-offshore-ecosystem": 4,
  "chemicals-materials-advanced-manufacturing-ecosystem": 5,
  "infrastructure-construction-smart-cities-ecosystem": 6,
  "agriculture-agritech-ecosystem": 4,
  "mining-metals-resources-ecosystem": 5,
  "digital-transformation-enterprise-technology-ecosystem": 6,
};

const DEFAULT_SECTOR_COUNTRY_TOTAL = 5;

export function getStaticSectorCountryCount(slug: string): number {
  return STATIC_SECTOR_COUNTRY_COUNTS[slug] ?? DEFAULT_SECTOR_COUNTRY_TOTAL;
}

export function getStaticSectorCountryLabel(slug: string): string {
  return `${getStaticSectorCountryCount(slug)}+`;
}

export type StaticSectorSuccessStories = {
  introductions: number;
  businessMeetings: number;
  crossBorderConnections: number;
};

/** Static success story counts per sector (introductions 8–10, meetings 5–7, cross-border 4–7). */
export const STATIC_SECTOR_SUCCESS_STORIES: Record<string, StaticSectorSuccessStories> = {
  "automotive-mobility-ecosystem": { introductions: 8, businessMeetings: 6, crossBorderConnections: 5 },
  "industrial-automation-smart-manufacturing-ecosystem": { introductions: 10, businessMeetings: 7, crossBorderConnections: 7 },
  "textile-apparel-fashion-manufacturing-ecosystem": { introductions: 8, businessMeetings: 5, crossBorderConnections: 4 },
  "renewable-energy-electrification-ecosystem": { introductions: 9, businessMeetings: 6, crossBorderConnections: 6 },
  "food-beverage-fmcg-ecosystem": { introductions: 10, businessMeetings: 7, crossBorderConnections: 7 },
  "healthcare-pharma-medtech-ecosystem": { introductions: 9, businessMeetings: 6, crossBorderConnections: 5 },
  "maritime-ports-offshore-ecosystem": { introductions: 8, businessMeetings: 5, crossBorderConnections: 4 },
  "chemicals-materials-advanced-manufacturing-ecosystem": { introductions: 10, businessMeetings: 7, crossBorderConnections: 6 },
  "infrastructure-construction-smart-cities-ecosystem": { introductions: 9, businessMeetings: 6, crossBorderConnections: 7 },
  "agriculture-agritech-ecosystem": { introductions: 8, businessMeetings: 5, crossBorderConnections: 5 },
  "mining-metals-resources-ecosystem": { introductions: 9, businessMeetings: 7, crossBorderConnections: 6 },
  "digital-transformation-enterprise-technology-ecosystem": { introductions: 10, businessMeetings: 6, crossBorderConnections: 7 },
};

const DEFAULT_SECTOR_SUCCESS_STORIES: StaticSectorSuccessStories = {
  introductions: 9,
  businessMeetings: 6,
  crossBorderConnections: 5,
};

export function getStaticSectorSuccessStories(slug: string): StaticSectorSuccessStories {
  return STATIC_SECTOR_SUCCESS_STORIES[slug] ?? DEFAULT_SECTOR_SUCCESS_STORIES;
}

export function getStaticSectorSuccessStoriesTotal(slug: string): number {
  const stories = getStaticSectorSuccessStories(slug);
  return stories.introductions + stories.businessMeetings + stories.crossBorderConnections;
}

export function getStaticSectorSuccessStoriesLabel(slug: string): string {
  return `${getStaticSectorSuccessStoriesTotal(slug)}+`;
}

export type StaticSectorCompanySegment = {
  key: string;
  label: string;
  count: number;
  percent: number;
};

export type StaticSectorCompanyStats = {
  total: number;
  totalLabel: string;
  segments: StaticSectorCompanySegment[];
};

const DEFAULT_SECTOR_COMPANY_TOTAL = 33;

function buildSectorCompanySegments(total: number): StaticSectorCompanySegment[] {
  const segments = COMPANY_SEGMENT_DEFS.map((def) => ({
    key: def.key,
    label: def.label,
    count: Math.round((total * def.percent) / 100),
    percent: def.percent,
  }));

  const segmentSum = segments.reduce((sum, segment) => sum + segment.count, 0);
  if (segmentSum !== total && segments.length > 0) {
    segments[segments.length - 1].count += total - segmentSum;
  }

  return segments;
}

export function getStaticSectorCompanyStats(slug: string): StaticSectorCompanyStats {
  const total = STATIC_SECTOR_COMPANY_COUNTS[slug] ?? DEFAULT_SECTOR_COMPANY_TOTAL;

  return {
    total,
    totalLabel: `${total}+`,
    segments: buildSectorCompanySegments(total),
  };
}

/** Placeholder opportunity rows when a sector has no live company sub-industry data yet. */
export const STATIC_SECTOR_OPPORTUNITY_SUB_INDUSTRIES: Record<string, string[]> = {
  "automotive-mobility-ecosystem": [
    "AUTOMOTIVE_EV_POWERTRAIN_DRIVETRAIN",
    "AUTOMOTIVE_BATTERIES_CHARGING",
    "AUTOMOTIVE_ELECTRONICS",
    "AUTOMOTIVE_TIER1_TIER2_COMPONENTS",
    "DEFENCE_AERO_ELECTRONICS_SYSTEMS",
  ],
  "industrial-automation-smart-manufacturing-ecosystem": [
    "MANUFACTURING_FACTORY_AUTOMATION_ROBOTICS",
    "MANUFACTURING_INDUSTRIAL_CONTROLS_PLCS",
    "MANUFACTURING_SMART_MANUFACTURING_INDUSTRY_4",
    "MANUFACTURING_SENSORS_INSTRUMENTATION",
    "MACHINERY_MACHINE_TOOLS_CNC",
  ],
  "textile-apparel-fashion-manufacturing-ecosystem": [
    "TEXTILES_TECHNICAL_INDUSTRIAL",
    "TEXTILES_NON_WOVEN_COMPOSITES",
    "TEXTILES_PROTECTIVE_FABRICS",
    "TEXTILES_PROCESSING_FINISHING",
    "TEXTILES_APPAREL_GARMENTS",
  ],
  "renewable-energy-electrification-ecosystem": [
    "ENERGY_RENEWABLE_EQUIPMENT",
    "ENERGY_STORAGE_SYSTEMS",
    "ENERGY_TRANSMISSION_DISTRIBUTION",
    "CLIMATE_CLEAN_MANUFACTURING",
    "CLIMATE_ENERGY_EFFICIENCY",
  ],
  "food-beverage-fmcg-ecosystem": [
    "AGRI_FOOD_PROCESSING_PACKAGING",
    "AGRI_COLD_CHAIN_STORAGE",
    "CONSUMER_HOME_PERSONAL_CARE",
    "PACKAGING_FLEXIBLE",
    "PACKAGING_MACHINERY_AUTOMATION",
  ],
  "healthcare-pharma-medtech-ecosystem": [
    "HEALTHCARE_MEDICAL_DEVICES",
    "HEALTHCARE_DIAGNOSTICS_IMAGING",
    "HEALTHCARE_LIFE_SCIENCES_TOOLS",
    "HEALTHCARE_PHARMA_MANUFACTURING",
    "HEALTHCARE_HOSPITAL_INFRASTRUCTURE",
  ],
  "maritime-ports-offshore-ecosystem": [
    "MARITIME_SHIPBUILDING_REPAIR",
    "MARITIME_MARINE_EQUIPMENT_SYSTEMS",
    "MARITIME_PORT_INFRASTRUCTURE_HANDLING",
    "MARITIME_LOGISTICS_TECHNOLOGY",
    "MARITIME_OFFSHORE_STRUCTURES_SERVICES",
  ],
  "chemicals-materials-advanced-manufacturing-ecosystem": [
    "CHEMICALS_SPECIALTY_PERFORMANCE",
    "CHEMICALS_INDUSTRIAL_GASES",
    "CHEMICALS_COATINGS_ADHESIVES_SEALANTS",
    "PLASTICS_INJECTION_MOULDING",
    "PLASTICS_POLYMER_COMPOUNDING",
  ],
  "infrastructure-construction-smart-cities-ecosystem": [
    "CONSTRUCTION_INFRASTRUCTURE_DEVELOPMENT",
    "CONSTRUCTION_INDUSTRIAL",
    "CONSTRUCTION_EPC_CONTRACTORS",
    "CONSTRUCTION_PRE_ENGINEERED_BUILDINGS",
    "CONSTRUCTION_MATERIALS_SYSTEMS",
  ],
  "agriculture-agritech-ecosystem": [
    "AGRI_MACHINERY_EQUIPMENT",
    "AGRI_INPUTS_SEEDS_FERTILISERS",
    "AGRI_PRECISION_FARMING_TECH",
    "AGRI_FOOD_PROCESSING_PACKAGING",
    "AGRI_COLD_CHAIN_STORAGE",
  ],
  "mining-metals-resources-ecosystem": [
    "METALS_FERROUS_NON_FERROUS",
    "METALS_MINING_EQUIPMENT_SERVICES",
    "METALS_MINERAL_PROCESSING",
    "METALS_FOUNDRIES_CASTINGS",
    "METALS_METAL_FABRICATION",
  ],
  "digital-transformation-enterprise-technology-ecosystem": [
    "DIGITAL_ENTERPRISE_SOFTWARE_SAAS",
    "DIGITAL_APIS_PLATFORMS",
    "DIGITAL_AI_DATA_ANALYTICS",
    "DIGITAL_CYBERSECURITY",
    "DIGITAL_B2B_PLATFORMS",
  ],
};

const DEFAULT_STATIC_OPPORTUNITY_SUB_INDUSTRIES = [
  "MANUFACTURING_FACTORY_AUTOMATION_ROBOTICS",
  "MANUFACTURING_INDUSTRIAL_CONTROLS_PLCS",
  "DIGITAL_ENTERPRISE_SOFTWARE_SAAS",
  "ENERGY_RENEWABLE_EQUIPMENT",
  "CONSTRUCTION_INFRASTRUCTURE_DEVELOPMENT",
];

export function getStaticSectorOpportunitySubIndustries(slug: string): string[] {
  return STATIC_SECTOR_OPPORTUNITY_SUB_INDUSTRIES[slug] ?? DEFAULT_STATIC_OPPORTUNITY_SUB_INDUSTRIES;
}

export const CARD_BORDER = "1px solid #E2E8F0";
